import { FormEvent, useMemo, useState } from "react";
import { Check, X } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { apiFetch } from "@/lib/api";
import { useToast } from "@/hooks/useToast";
import { MeasurementRow } from "@/types/measurements";

interface Props {
  open: boolean;
  periodId: string;
  rows: MeasurementRow[];
  onClose: () => void;
  onSaved: () => void;
}

type CellState = "idle" | "saving" | "success" | "error";
interface CellStatus { state: CellState; error?: string; }

export function BulkEditModal({ open, periodId, rows, onClose, onSaved }: Props) {
  const editable = rows.filter((r) => r.editable);
  const [values, setValues] = useState<Record<string, string>>({});
  const [statuses, setStatuses] = useState<Record<string, CellStatus>>({});
  const { push } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const byLocation = useMemo(() => {
    const m = new Map<string, MeasurementRow[]>();
    for (const r of editable) {
      const arr = m.get(r.location) ?? [];
      arr.push(r);
      m.set(r.location, arr);
    }
    return m;
  }, [editable]);

  function key(r: MeasurementRow) { return `${r.location}:${r.utility}`; }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    const [year, month] = periodId.split("-").map(Number);
    const targets = editable.filter((r) => {
      const k = key(r);
      const v = values[k];
      return v !== undefined && v !== "" && statuses[k]?.state !== "success";
    });

    setStatuses((curr) => {
      const next = { ...curr };
      for (const r of targets) next[key(r)] = { state: "saving" };
      return next;
    });

    let allOk = true;
    for (const r of targets) {
      const k = key(r);
      try {
        const method = r.missing || r.value === null ? "POST" : "PUT";
        await apiFetch("/api/utility/reading", {
          method,
          body: JSON.stringify({ year, month, location: r.location, utility: r.utility, value: values[k] }),
        });
        setStatuses((curr) => ({ ...curr, [k]: { state: "success" } }));
      } catch (err) {
        allOk = false;
        setStatuses((curr) => ({ ...curr, [k]: { state: "error", error: (err as Error).message } }));
      }
    }

    setSubmitting(false);
    if (allOk) {
      push({ message: `Zapisano ${targets.length} pomiarów`, tone: "success" });
      onSaved();
      onClose();
    } else {
      push({ message: "Część pomiarów nie została zapisana", tone: "warning" });
      onSaved(); // still refresh — partial success is real
    }
  }

  function cellPill(r: MeasurementRow) {
    const s = statuses[key(r)];
    if (!s) return null;
    if (s.state === "saving") return <Badge tone="muted">…</Badge>;
    if (s.state === "success") return <Badge tone="success"><Check className="size-3" /></Badge>;
    if (s.state === "error") return <Badge tone="danger" title={s.error}><X className="size-3" /></Badge>;
    return null;
  }

  return (
    <Modal open={open} onClose={onClose} title="Edycja zbiorcza pomiarów" size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Zamknij</Button>
          <Button onClick={submit} loading={submitting}>Zapisz wszystkie</Button>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        {Array.from(byLocation.entries()).map(([loc, locRows]) => (
          <div key={loc}>
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">{loc}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {locRows.map((r) => {
                const k = key(r);
                return (
                  <div key={k} className="flex items-end gap-2">
                    <div className="flex-1">
                      <Input id={`b-${k}`} label={`${r.utility} (${r.unit})`}
                             placeholder={r.missing ? "—" : r.value ?? ""}
                             inputMode="decimal"
                             value={values[k] ?? ""}
                             onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))} />
                    </div>
                    {cellPill(r) && <div className="pb-1.5">{cellPill(r)}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </form>
    </Modal>
  );
}
